import { useCallback, useRef } from 'react';
import { captureLogger as logger } from './configure/logger-facade';

export const useAlertSound = (cooldownTime = 10000, soundSrc = '/alert.mp3') => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const lastPlayedRef = useRef<number>(0);

  if (!audioRef.current && typeof Audio !== 'undefined') {
    audioRef.current = new Audio(soundSrc);
    audioRef.current.preload = 'auto';
    audioRef.current.load();
  }

  const playAlert = useCallback(async (): Promise<boolean> => {
    const now = Date.now();
    const elapsedTime = now - lastPlayedRef.current;
    if (elapsedTime < cooldownTime) {
      // The ref outlives individual recordings (the hook's host component is
      // long-lived), so back-to-back alerts across recordings mute here too.
      logger.info('[AlertSound] Skipped: within cooldown', {
        elapsedMs: elapsedTime,
        cooldownMs: cooldownTime,
      });
      return false;
    }
    if (!audioRef.current) return false;
    audioRef.current.currentTime = 0;
    lastPlayedRef.current = now;
    try {
      await audioRef.current.play();
      return true;
    } catch {
      logger.warn('[AlertSound] Playback failed');
      return false;
    }
  }, [cooldownTime]);

  return { playAlert };
};
